import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Page from '@glowforge/ui-common/components/contentful-views/page';
import { pagePropType } from '../../prop-types/contentful';
import CustomPageMetadata from '../../components/custom-page-metadata';
import { getPage } from '../../api/contentful';
import { fetchProducts } from '../../api/storefront';
import { fetchReviewsByProductId } from '../../api/yotpo';
import { reviewsPropType } from '../../prop-types/yotpo';
import { productPropType } from '../../prop-types/storefront';
import YotpoWidgetContainer from '../../components/yotpo/yotpo-widget-container';
import GlowforgeProductJsonLd from '../../components/glowforge-product/glowforge-product-json-ld';
import '@glowforge/ui-common/initializers/contentful-page';

import './reviews-page.less';

/**
 * Yotpo reviews use a generic `generic-GF` ID to pull in reviews for all
 * Glowforge products
 */
const genericGlowforgeYotpoId = 'generic-GF';

/**
 * Dig into Contentful data and find the JSON-LD template for Glowforge Plus. It
 * was recommended that we use Glowforge Plus in our page metadata on our
 * generic reviews page.
 * @param {object[]} sections
 */
const getJsonLdTemplate = (sections) => {
  const plusSection = _.find(sections, { fields: { id: 'glowforge-plus-section' } });
  const plusSectionSections = _.get(plusSection, 'fields.sections', []);
  // eslint-disable-next-line max-len
  const jsonLdTemplateSection = _.find(plusSectionSections, { fields: { id: 'glowforge-plus-json-ld-template' } });
  return _.get(jsonLdTemplateSection, 'fields.text', '');
};

/**
 * Adds a Yotpo embed to the page and inserts JSON-LD review metadata into the
 * page header to improve search engine results.
 * @param {object} props
 * @param {object[]} props.sections Contentful sections
 * @param {{ [product: string]: object }} props.products Product information
 * from the storefront API, used in JSON-LD
 * @param {object} props.reviews Yotpo review data inserted into JSON-LD
 * template
 */
const YotpoWidget = ({
  sections,
  products,
  reviews,
}) => {
  const jsonLdTemplate = getJsonLdTemplate(sections);
  return (
    <>
      <GlowforgeProductJsonLd
        jsonLdTemplate={jsonLdTemplate}
        product={products.plus}
        reviews={reviews} />
      <YotpoWidgetContainer>
        <div className='YotpoWidget fullWidth'>
          <div
            className='yotpo yotpo-main-widget'
            data-product-id={genericGlowforgeYotpoId}
            data-name='Glowforge'
            data-url='https://glowforge.com/order' />
        </div>
      </YotpoWidgetContainer>
    </>
  );
};

YotpoWidget.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  products: PropTypes.objectOf(productPropType).isRequired,
  reviews: reviewsPropType.isRequired,
};

const ReviewsPage = ({ page, products, reviews }) => {
  const customSections = {
    'yotpo-reviews': {
      component: YotpoWidget,
      props: {
        reviews,
        products,
      },
    },
  };

  return (
    <>
      <CustomPageMetadata page={page} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Page className='Reviews' {...page.fields} customSections={customSections} />
    </>
  );
};

ReviewsPage.getInitialProps = async () => {
  const [
    page,
    products,
    reviews,
  ] = await Promise.all([
    getPage('yotpo-reviews-page'),
    fetchProducts(),
    fetchReviewsByProductId(genericGlowforgeYotpoId),
  ]);
  return { page, products, reviews };
};

ReviewsPage.propTypes = {
  page: pagePropType.isRequired,
  products: PropTypes.objectOf(productPropType).isRequired,
  reviews: reviewsPropType.isRequired,
};

export default ReviewsPage;
